import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Roles } from '~/enums/Roles';
import { useOnClickOutside } from '~/helpers/helpers';
import { Comment } from '~/types/comment';
import { ReactComponent as Ellipsis } from '~/utils/images/ellipsis.svg';
import { CommentFilterType } from '~/containers/Comments/Comments';
import useIsMounted from '~/hooks/isMounted';
import { useSearchQuery } from '~/hooks/useSearchQuery';
import { VENDOR_NAME_KEY } from '~/theme/vendorConstants';
import { ReactionType, ReactionConfig, DEFAULT_REACTIONS } from '~/types/reaction';
import ReactionPicker from '~/components/ReactionPicker/ReactionPicker';
import ReactionSummary from '~/components/ReactionSummary/ReactionSummary';
import { hiddenByLabel } from '~/helpers/comment';


interface IProps {
  comment: Comment;
  index?: number;
  parentComment?: Comment;
  filter: number;
  isThread: boolean;
  currentUserRole: string;
  selectedCommentId: string;
  children?: Object[];
  setSelectedCommentId: (selectedCommentId: string) => void;
  onReplyClick: (comment: Comment) => void;
  onDeleteClick: (comment: Comment) => void;
  notifyResident: (comment: Comment) => void;
  setShowThreadModal: (showThreadModal: boolean) => void;
  hideComment: (comment: Comment) => void;
  pinComment: (comment: Comment) => void;
  onOpenThreadClick: (parentComment: Comment) => void;
  markGreatContent: (comment: Comment) => void;
  togglePublicFacing: (comment: Comment) => void;
  /** Omitted when the building is on Marketplace v2 — the old thread is closed. */
  moveToMarketplace?: (comment: Comment) => void;
  blockUser?: (comment: Comment) => void;
  isIntegratedBuilding?: boolean;
  commentType: CommentFilterType;
  preventIndent?: boolean;
  onReact?: (commentId: string, reactionName: ReactionType | null, isRemoving: boolean, parentCommentId?: string) => void;
  currentUserId?: string;
  isReactionLoading?: boolean;
  reactions?: ReactionConfig[];
}
const CommentBox = (props: IProps) => {
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => {
    setSelectedCommentId('');
  });
  const [hasNavigatedToComment, sethasNavigatedToComment] =
    useState<boolean>(false);

  const {
    comment,
    index,
    parentComment,
    filter,
    isThread,
    currentUserRole,
    selectedCommentId,
    children,
    setSelectedCommentId,
    onReplyClick,
    onDeleteClick,
    notifyResident,
    setShowThreadModal,
    hideComment,
    pinComment,
    onOpenThreadClick,
    markGreatContent,
    togglePublicFacing,
    moveToMarketplace,
    blockUser,
    commentType,
    isIntegratedBuilding,
    preventIndent = false,
    onReact,
    currentUserId,
    isReactionLoading = false,
    reactions = DEFAULT_REACTIONS
  } = props;

  const [showReactionPicker, setShowReactionPicker] = useState(false);

  const prettifyTime = (time: string) => {
    return moment(time).fromNow().includes('second') === false &&
      moment(time).fromNow().includes('minute') === false &&
      moment(time).fromNow().includes('hour') === false &&
      moment(time).fromNow().includes('day') === false
      ? moment(time).format('ll')
      : moment(time).fromNow();
  };

  const commentRef = useRef<HTMLElement>();
  const isMounted = useIsMounted();
  const query = useSearchQuery();

  let thisClass =
    comment.parent && !preventIndent ? 'comment-child' : 'comment';

  // Top-level comments have no parent to gate on. A reply is publishable only
  // once its parent is already public — the parent arrives either as the
  // `parentComment` prop (thread view) or nested on the comment itself.
  const parentOfComment = parentComment ?? comment.parent;
  const isParentPublicFacing =
    !parentOfComment || !!parentOfComment.isPublicFacing;

  useEffect(() => {
    if (!hasNavigatedToComment && query?.commentId == comment.uuid) {
      if (commentRef?.current) {
        commentRef?.current?.scrollIntoView({
          block: 'center',
          inline: 'center'
        });
        commentRef.current.className = 'comment-blink';
        sethasNavigatedToComment(true);
      }
    }
  }, [isMounted]);

  return (
    <div ref={commentRef as any} id={`comment-${comment.uuid}`}>
      <div className={thisClass}>
        {comment.parent &&
          parentComment &&
          index !== parentComment.children.length - 1 && (
            <div className='left-line' />
          )}
        {parentComment !== undefined && <div className='curve' />}
        <img
          className='avatar margin-right-8'
          alt='avatar'
          src={comment.user.profileImage}
        />
        {parentComment === undefined &&
          comment.children &&
          comment.children.length > 0 && <div className='line' />}
        <div>
          <div className='checkbox-row'>
            <p className='comments-container-txt margin-bottom-4'>
              {`${comment.user.firstName} ${comment.user.lastName}`}
            </p>
            {comment.removed && (
              <p className='removed-label checkbox-text margin-left-8 warning-background'>
                {`Comment has been DELETED by ${comment.removedBy || 'ADMIN'}`}
              </p>
            )}
            {comment.hidden && (
              <p className='removed-label checkbox-text margin-left-8 warning-background'>
                {`Comment has been HIDDEN by ${hiddenByLabel(comment.hiddenBy)}`}
              </p>
            )}
            {((parentComment && parentComment.removed) ||
              (comment.parent && comment.parent.removed)) && (
              <p className='removed-label checkbox-text margin-left-8 warning-background'>
                This reply is NOT displayed. PARENT comment has been DELETED.
              </p>
            )}
            {(parentComment && parentComment.hidden) ||
              (comment.parent && comment.parent.hidden && (
                <p className='removed-label checkbox-text margin-left-8 cobu warning-background'>
                  This reply is NOT displayed. PARENT comment has been HIDDEN.
                </p>
              ))}
            {comment.greatContent && currentUserRole !== Roles.Pm && currentUserRole !== Roles.PM_FREE && (
              <p className='removed-label checkbox-text margin-left-8 cobu-dark-blue-background'>
                This is GREAT content
              </p>
            )}
            {comment.pinComment && currentUserRole !== Roles.Pm && currentUserRole !== Roles.PM_FREE && (
              <p className='removed-label checkbox-text margin-left-8 cobu-turquoise-background'>
                Pinned Post
              </p>
            )}
          </div>
          <p
            className={
              (parentComment && parentComment.hidden) ||
              (parentComment && parentComment.removed) ||
              comment.removed ||
              comment.hidden
                ? 'comments-container-content-hidden'
                : 'comments-container-content'
            }
          >
            {comment.images?.length ? (
              <>
                {comment.contents && <p>{comment.contents}</p>}
                {comment.images?.length > 0 && (
                  <div style={{ overflow: 'auto', marginTop: 16 }}>
                    {comment.images.map((commentImage) => (
                      <img
                        src={commentImage.image}
                        className={
                          (parentComment && parentComment.hidden) ||
                          (parentComment && parentComment.removed) ||
                          comment.removed ||
                          comment.hidden
                            ? 'bw'
                            : ''
                        }
                        alt='comment img'
                        width='80'
                        height={'80'}
                        style={{ borderRadius: 10, marginRight: 8 }}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              comment.contents
            )}
          </p>
          {filter !== 0 && !isThread ? (
            <>
              <p className='comment-time'>{prettifyTime(comment.createdAt)}</p>
              <p
                className='comment-reply-btn margin-left-16'
                onClick={() => onOpenThreadClick(comment)}
              >
                Open thread
              </p>
            </>
          ) : !comment.parent ? (
            <>
              <p className='comment-time'>{prettifyTime(comment.createdAt)}</p>
              <p
                className='comment-reply-btn margin-left-16'
                onClick={() => {
                  onReplyClick(comment);
                }}
              >
                Reply
              </p>
            </>
          ) : (
            <p className='comment-time'>{prettifyTime(comment.createdAt)}</p>
          )}
          {!comment.removed && !comment.hidden && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              {onReact && (() => {
                const activeConfig = comment.myReaction
                  ? reactions.find((r) => r.type === comment.myReaction)
                  : null;
                const handleReaction = (reaction: ReactionType | null) => {
                  setShowReactionPicker(false);
                  const isRemoving = reaction === null || reaction === comment.myReaction;
                  onReact(
                    comment.uuid,
                    isRemoving ? null : reaction,
                    isRemoving,
                    comment.parent ? comment.parent.uuid : undefined
                  );
                };
                return (
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    {showReactionPicker && !isReactionLoading && (
                      <ReactionPicker
                        currentReaction={comment.myReaction}
                        onSelectReaction={handleReaction}
                        onClose={() => setShowReactionPicker(false)}
                        reactions={reactions}
                      />
                    )}
                    {isReactionLoading ? (
                      <button
                        disabled
                        style={{
                          background: 'transparent',
                          border: '1px solid #ccc',
                          borderRadius: 14,
                          padding: '2px 8px',
                          cursor: 'not-allowed',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 24
                        }}
                      >
                        <span style={{
                          width: 12,
                          height: 12,
                          border: '2px solid #ccc',
                          borderTopColor: '#999',
                          borderRadius: '50%',
                          display: 'inline-block',
                          animation: 'spin 0.7s linear infinite'
                        }} />
                      </button>
                    ) : comment.myReaction && activeConfig ? (
                      <button
                        onClick={() => setShowReactionPicker((prev) => !prev)}
                        style={{
                          background: `${activeConfig.color}22`,
                          border: `1.5px solid ${activeConfig.color}`,
                          borderRadius: 14,
                          padding: '2px 10px',
                          cursor: 'pointer',
                          fontSize: 15,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                        title='Change or remove reaction'
                      >
                        {activeConfig.imageUrl
                          ? <img src={activeConfig.imageUrl} alt={activeConfig.type} style={{ width: 18, height: 18, objectFit: 'contain' }} />
                          : <span>{activeConfig.emoji}</span>
                        }
                        {!!comment.reactionsCount && (
                          <span style={{ fontSize: 12, color: activeConfig.color, fontWeight: 600 }}>
                            {comment.reactionsCount}
                          </span>
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowReactionPicker((prev) => !prev)}
                        style={{
                          background: 'transparent',
                          border: '1px solid #ccc',
                          borderRadius: 14,
                          padding: '2px 8px',
                          cursor: 'pointer',
                          fontSize: 14,
                          color: '#aaa',
                          lineHeight: 1.4
                        }}
                        title='Add reaction'
                      >
                        ☺
                      </button>
                    )}
                  </div>
                );
              })()}
              {!!comment.reactionsCount && (!comment.myReaction || !onReact) && (
                <ReactionSummary
                  reactionsCount={comment.reactionsCount}
                  byReactionUserIds={comment.byReactionUserIds || {}}
                  reactionsSorted={comment.reactionsSorted}
                  reactions={reactions}
                />
              )}
            </div>
          )}
        </div>
        {(filter === 0 || isThread) &&
          !!currentUserRole &&
          (currentUserRole === Roles.Pm ||
            currentUserRole === Roles.PM ||
            currentUserRole === Roles.PM_FREE ||
            currentUserRole === Roles.Vcm ||
            currentUserRole === Roles.Superadmin) && (
            <div
              className='ellipsis'
              onClick={() => setSelectedCommentId(comment.uuid)}
            >
              <Ellipsis />
              {selectedCommentId === comment.uuid && (
                <div className='small-list' ref={ref}>
                  <div className='more-actions-dropdown'>
                    {(() => {
                      const role = currentUserRole as string;
                      const isPartnerMode = !!sessionStorage.getItem(VENDOR_NAME_KEY);
                      const isPMPaid =
                        role === Roles.Pm || role === Roles.PM;
                      const isPMFree = role === Roles.PM_FREE;
                      const isVCMOrSuperadmin =
                        role === Roles.Vcm || role === Roles.Superadmin;
                      // PMs hide rather than delete — deletion signals removal to the resident.
                      const baseOptions = [
                        {
                          label: comment.hidden ? 'Unhide' : 'Hide',
                          action: () => hideComment(comment)
                        },
                        ...(!isPartnerMode && isVCMOrSuperadmin ? [{
                          label: comment.removed ? 'Undelete' : 'Delete',
                          action: () => onDeleteClick(comment)
                        }] : [])
                      ];
                      // Backend authz: VCM/Superadmin anywhere, or PM/PM_FREE on an integrated
                      // building. Only residents can be blocked. `blockUser` is passed by
                      // the parent only when the building tier supports it.
                      const canBlock =
                        !!blockUser &&
                        comment.user?.type === 'resident' &&
                        (isVCMOrSuperadmin ||
                          ((isPMPaid || isPMFree) && !!isIntegratedBuilding));
                      const blockOption = canBlock
                        ? [
                            {
                              label: 'Block user',
                              action: () => blockUser(comment)
                            }
                          ]
                        : [];
                      const extraOptions = isPMPaid
                        ? [
                            {
                              label: 'Notify the Resident',
                              action: () => notifyResident(comment)
                            },
                            ...blockOption
                          ]
                        : isVCMOrSuperadmin
                        ? [
                            {
                              label: comment.greatContent
                                ? 'Unmark as Great Content'
                                : 'Mark as Great Content',
                              action: () => markGreatContent(comment)
                            },
                            // Gathering comments have no isPublicFacing column,
                            // and hidden/deleted content is never published.
                            // A reply only qualifies once its parent is public:
                            // the snapshot nests by parent and drops any child
                            // whose parent is absent, so it would be a silent no-op.
                            ...(commentType !== CommentFilterType.GATHERING &&
                            !comment.hidden &&
                            !comment.removed &&
                            isParentPublicFacing
                              ? [
                                  {
                                    label: comment.isPublicFacing
                                      ? 'Remove from website'
                                      : 'Add to website',
                                    action: () => togglePublicFacing(comment)
                                  }
                                ]
                              : []),
                            {
                              label: comment.pinComment ? 'Unpin' : 'Pin',
                              action: () => pinComment(comment)
                            },
                            ...(moveToMarketplace
                              ? [
                                  {
                                    label: 'Move to Marketplace',
                                    action: () => moveToMarketplace(comment)
                                  }
                                ]
                              : []),
                            ...blockOption
                          ]
                        : isPMFree
                        ? [...blockOption]
                        : [];
                      return [...baseOptions, ...extraOptions];
                    })().map((option, index) => (
                      <div
                        key={index}
                        className='more-actions-option pointer'
                        onClick={() => {
                          setShowThreadModal(false);
                          option.action();
                          setSelectedCommentId('');
                        }}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
      </div>
      <div>{children && children}</div>
    </div>
  );
};

export default CommentBox;
